import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './App.css';
import { saveContactSubmission } from './firebase';
import alboLogo from './assets/albo-logo.svg';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    // Scroll reveal animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observer.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        type: 'error',
        message: 'Please fill in all required fields.'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        type: 'error',
        message: 'Please enter a valid email address.'
      });
      return;
    }


    // Save to Firebase
    const result = await saveContactSubmission(formData);

    if (result.success) {
      setFormStatus({
        type: 'success',
        message: 'Thank you! We\'ll get back to you soon.'
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
        setFormStatus({ type: '', message: '' });
        setIsModalOpen(false);
      }, 3000);
    } else {
      setFormStatus({
        type: 'error',
        message: 'Failed to submit. Please try again or email us directly.'
      });
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setFormStatus({ type: '', message: '' });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormStatus({ type: '', message: '' });
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container navbar-content">
          <a href="#home" className="logo">
            <img src={alboLogo} alt="Albo Labs" className="logo-image" />
            Albo<span className="logo-accent">Labs</span>
          </a>
          <ul className="nav-links">
            <li><a href="#about" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a></li>
            <li><a href="#services" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Services</a></li>
            <li><a href="#values" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('values'); }}>Values</a></li>
            <li><a href="#contact" className="nav-link" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="container hero-content">
          <div className="hero-tagline">Build Clean. Think Clear.</div>
          <h1>Modern Software & Digital Innovation</h1>
          <p className="hero-description">
            We craft clean, intelligent, and user-friendly solutions that transform ideas into purposeful digital experiences.
          </p>
          <div className="hero-cta">
            <button className="btn btn-primary" onClick={() => scrollToSection('services')}>
              Explore Services
              <span>→</span>
            </button>
            <button className="btn btn-secondary" onClick={() => scrollToSection('contact')}>
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section about">
        <div className="container">
          <div className="about-content">
            <div className="about-text scroll-reveal">
              <h2>Who We Are</h2>
              <p>
                Albo Labs is a modern software and digital innovation company focused on building clean,
                intelligent, and user-friendly solutions for businesses. Guided by the philosophy
                <strong> "Build Clean. Think Clear."</strong>, we emphasize clarity, precision, and
                thoughtful design in every project we deliver.
              </p>
              <p>
                We work with individuals, startups, and established organizations to help them bring
                ideas to life through purposeful digital experiences. With a strong commitment to
                quality and transparency, we blend creativity with engineering discipline to create
                solutions that are simple, efficient, and future-ready.
              </p>
              <div className="about-highlight">
                <h3>Our Philosophy</h3>
                <p>
                  Every line of code, every design decision, every user interaction is crafted with
                  intention. We don't just build software—we create experiences that make sense,
                  work seamlessly, and stand the test of time.
                </p>
              </div>
            </div>
            <div className="about-visual scroll-reveal">
              <div className="about-card about-card-1">
                <h4>Clean Code</h4>
                <p>Maintainable, scalable, and elegant solutions</p>
              </div>
              <div className="about-card about-card-2">
                <h4>Clear Thinking</h4>
                <p>Strategic approach to every challenge</p>
              </div>
              <div className="about-card about-card-3">
                <h4>Future-Ready</h4>
                <p>Built to evolve with your business</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section services">
        <div className="container">
          <div className="services-header scroll-reveal">
            <h2>Services We Offer</h2>
            <p>
              Comprehensive digital solutions tailored to your business needs,
              delivered with precision and care.
            </p>
          </div>
          <div className="services-grid">
            <div className="service-card scroll-reveal">
              <div className="service-icon">💻</div>
              <h3>Custom Software Development</h3>
              <p>
                Tailored digital systems that match your unique workflows and goals,
                ensuring reliability, scalability, and long-term value.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">📱</div>
              <h3>Mobile Application Development</h3>
              <p>
                End-to-end mobile app development for businesses looking to modernize,
                expand, or engage users through intuitive and clean mobile experiences.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">🌐</div>
              <h3>Web Application Development</h3>
              <p>
                Creation of seamless, high-performance web applications designed for
                usability, clarity, and modern digital standards.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">🤖</div>
              <h3>AI & Automation Solutions</h3>
              <p>
                Implementation of intelligent automation workflows and AI-powered features
                to help businesses operate faster, smarter, and with greater efficiency.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">📊</div>
              <h3>ERP & Business Systems</h3>
              <p>
                Designing structured, organized, and efficient business systems that
                streamline operations, strengthen data flow, and support long-term growth.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">🎨</div>
              <h3>UI/UX & Digital Experience Design</h3>
              <p>
                Crafting minimal, modern, and user-focused interfaces that make digital
                journeys smooth, clear, and enjoyable.
              </p>
            </div>

            <div className="service-card scroll-reveal">
              <div className="service-icon">☁️</div>
              <h3>Cloud & Backend Architecture</h3>
              <p>
                Building scalable, secure, and stable infrastructure foundations to ensure
                businesses run smoothly and confidently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="values" className="section values">
        <div className="container values-content">
          <div className="values-header scroll-reveal">
            <h2>How Albo Labs Stands Out</h2>
            <p>
              Our commitment to excellence is reflected in everything we do,
              from initial concept to final delivery.
            </p>
          </div>
          <div className="values-grid">
            <div className="value-item scroll-reveal">
              <div className="value-icon">✨</div>
              <h3>Clean Design Philosophy</h3>
              <p>Modern, minimal aesthetics that prioritize clarity and user experience</p>
            </div>

            <div className="value-item scroll-reveal">
              <div className="value-icon">💬</div>
              <h3>Clarity & Transparency</h3>
              <p>Open communication and honest collaboration throughout the journey</p>
            </div>

            <div className="value-item scroll-reveal">
              <div className="value-icon">🎯</div>
              <h3>Long-term Impact</h3>
              <p>Solutions designed to grow and evolve with your business needs</p>
            </div>

            <div className="value-item scroll-reveal">
              <div className="value-icon">⚙️</div>
              <h3>Thoughtful Engineering</h3>
              <p>Careful, deliberate development—never rushed, always intentional</p>
            </div>

            <div className="value-item scroll-reveal">
              <div className="value-icon">👤</div>
              <h3>Usability First</h3>
              <p>Every feature is built with the end user's experience in mind</p>
            </div>

            <div className="value-item scroll-reveal">
              <div className="value-icon">🚀</div>
              <h3>Simplicity & Efficiency</h3>
              <p>Powerful solutions that remain simple, elegant, and easy to use</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="section cta">
        <div className="container cta-content">
          <h2>Ready to Build Something Great?</h2>
          <p>
            Let's transform your vision into a clean, intelligent digital solution
            that drives real business value.
          </p>
          <button className="btn btn-white" onClick={openModal}>
            Start Your Project
            <span>→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo-row">
                <img src={alboLogo} alt="Albo Labs" className="footer-logo" />
                <h3>Albo Labs</h3>
              </div>
              <p>
                Building clean, intelligent, and user-friendly solutions for the modern digital world.
              </p>
              <p style={{ marginTop: '1rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                Build Clean. Think Clear.
              </p>
            </div>

            <div className="footer-section">
              <h4>Services</h4>
              <ul className="footer-links">
                <li><a href="#services">Custom Software</a></li>
                <li><a href="#services">Mobile Apps</a></li>
                <li><a href="#services">Web Applications</a></li>
                <li><a href="#services">AI & Automation</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <ul className="footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#values">Our Values</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><Link to="/sealchat/privacy">Privacy Policy</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Connect</h4>
              <ul className="footer-links">
                <li><a href="#contact">Get in Touch</a></li>
                <li><a href="#contact">Start a Project</a></li>
                <li><a href="#contact">Careers</a></li>
                <li><Link to="/support">Support</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Albo Labs. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h2>Start Your Project</h2>
            <p className="modal-description">
              Tell us about your project and we'll get back to you within 24 hours.
            </p>

            <form onSubmit={handleFormSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (234) 567-890"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Project Details *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us about your project, goals, and timeline..."
                  rows="5"
                  required
                ></textarea>
              </div>

              {formStatus.message && (
                <div className={`form-status ${formStatus.type}`}>
                  {formStatus.message}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-submit">
                Send Message
                <span>→</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
